// src/modules/units/tyres/views/tyres.api.js
//
// The tyre subsystem's API client. Reads go through request(); every write goes
// through mutate(), which emits TYRE_EVENTS.CHANGED on success.
//
// Emitting here rather than at each call site is what makes the
// mutate -> emit CHANGED -> refetch -> re-render cycle unskippable. A view cannot
// forget to announce a change, and a half-finished sequence (record updated, then
// the action log rejected) still tells the rest of the module that something moved
// -- which is precisely the case the old per-view emits dropped on the floor.
import { getUserData } from '../../../auth/views/login/auth.api.js';
import { emitTyreEvent, TYRE_EVENTS } from '../shared/tyre-events.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';

async function request(path, options = {}) {
  const { token } = getUserData() || {};

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    credentials: 'include'
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return {
      success: false,
      status: response.status,
      message: data?.message || data?.error || 'Tyre request failed.',
      data
    };
  }

  return {
    success: true,
    status: response.status,
    data
  };
}

// Write-side wrapper. `reason` is a debugging label carried on the event; no
// subscriber branches on it, so a caller that omits it still gets a correct
// event -- the label can degrade, the notification cannot.
async function mutate(path, options, { externalTyreId, reason }) {
  const res = await request(path, options);

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.CHANGED, { externalTyreId, reason });
  }

  return res;
}

export async function fetchTyres(params = {}, { signal } = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });

  const qs = query.toString();
  return request(`/tyre${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    signal
  });
}

// Page through GET /api/tyre until a short page comes back.
//
// Stops on a short page rather than trusting `count`, whose semantics are still
// unverified against the live API (total matching rows, or size of this page?).
// MAX_PAGES is a seatbelt, not a limit anyone should reach; `capped` says so
// honestly rather than silently returning a partial fleet.
//
// This is the one pager in the module. The reports orchestrator and the
// dashboard's bin counts both use it, so there is a single place where "the
// whole fleet" is defined.
const ALL_PAGE_SIZE = 200;
const ALL_MAX_PAGES = 60;

export async function fetchAllTyres(params = {}, { signal } = {}) {
  const rows = [];
  let offset = 0;
  let pages = 0;
  let capped = false;

  for (; ;) {
    const res = await fetchTyres({ ...params, limit: ALL_PAGE_SIZE, offset }, { signal });
    if (!res.success) return res;

    const page = Array.isArray(res.data?.results) ? res.data.results : [];
    rows.push(...page);
    pages++;

    if (page.length < ALL_PAGE_SIZE) break;
    offset += ALL_PAGE_SIZE;
    if (pages >= ALL_MAX_PAGES) { capped = true; break; }
  }

  return { success: true, status: 200, data: { results: rows, count: rows.length, capped } };
}

export async function fetchTyre(externalTyreId) {
  return request(`/tyre/${encodeURIComponent(externalTyreId)}`, {
    method: 'GET'
  });
}

// ---------------------------------------------------------------------------
// Archive / restore
//
// "Archive" is the product word; the backend calls the same operation a soft
// delete (DELETE /tyre/{id}, GET /tyre/deleted, POST /tyre/{id}/restore). The
// translation lives here, once, so no view ever has to know the server's name
// for it. Nothing is destroyed: the record and its history are kept, the tyre
// is hidden from lists, exports and detail endpoints, and its ID cannot be
// reused until it is restored.
// ---------------------------------------------------------------------------

// Archive: 204 with no body on success. 409 means the tyre is still associated
// with a sensor or an active retread operation.
export async function archiveTyre(externalTyreId) {
  return mutate(`/tyre/${encodeURIComponent(externalTyreId)}`, {
    method: 'DELETE'
  }, { externalTyreId, reason: 'archive' });
}

// Restore: 200 with the restored record. The backend keeps the archive metadata
// and stamps restoredAt / restoredBy; it does NOT re-associate a sensor. 404 means
// the tyre is not found or is not archived (e.g. someone else restored it first).
export async function restoreTyre(externalTyreId) {
  return mutate(`/tyre/${encodeURIComponent(externalTyreId)}/restore`, {
    method: 'POST'
  }, { externalTyreId, reason: 'restore' });
}

// The archived-tyre list (administrator only). Only the query keys confirmed in
// the Swagger spec for GET /api/tyre/deleted are forwarded: the endpoint's prose
// says it "supports the same filters as GET /api/tyre", but only these four are
// documented, and an undocumented key is a silent no-op (the imeiNumber/unitId
// lesson). Anything else a caller passes is dropped here, deliberately.
const ARCHIVED_QUERY_KEYS = ['limit', 'offset', 'search', 'externalTyreId'];

export async function fetchArchivedTyres(params = {}, { signal } = {}) {
  const query = new URLSearchParams();
  ARCHIVED_QUERY_KEYS.forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') query.append(key, value);
  });
  const qs = query.toString();
  return request(`/tyre/deleted${qs ? `?${qs}` : ''}`, { method: 'GET', signal });
}

// Page through GET /api/tyre/deleted to completion — the archived-list parallel to
// fetchAllTyres(), same stop rule (a short page) and same seatbelt. The archived
// list needs the whole set because it filters by yard on the client: the endpoint
// does not document a yardId filter, so the yard scope is applied to the rows'
// own `yardId` instead (see archivedtyres.js). Archived tyres are a small,
// slow-growing set, so one sweep is cheap.
export async function fetchAllArchivedTyres(params = {}, { signal } = {}) {
  const rows = [];
  let offset = 0;
  let pages = 0;
  let capped = false;

  for (; ;) {
    const res = await fetchArchivedTyres({ ...params, limit: ALL_PAGE_SIZE, offset }, { signal });
    if (!res.success) return res;

    const page = Array.isArray(res.data?.results) ? res.data.results : [];
    rows.push(...page);
    pages++;

    if (page.length < ALL_PAGE_SIZE) break;
    offset += ALL_PAGE_SIZE;
    if (pages >= ALL_MAX_PAGES) { capped = true; break; }
  }

  return { success: true, status: 200, data: { results: rows, count: rows.length, capped } };
}

export async function saveTyre(payload, { reason = 'save' } = {}) {
  return mutate('/tyre', {
    method: 'POST',
    body: JSON.stringify(payload)
  }, { externalTyreId: payload?.externalTyreId, reason });
}

// Bulk upsert for the spreadsheet importer: POST /api/tyre once per payload, at
// most `concurrency` in flight, and ONE CHANGED for the whole call.
//
// Why not loop saveTyre(): each saveTyre() emits CHANGED, and every open listener
// (the dashboard's full-fleet sweep, the register, reports) refetches on each one —
// a 500-row import would trigger 500 sweeps. The emit still lives here, in the
// write layer, so the invariant holds: no caller can write without announcing it,
// and a partly-failed batch still announces the rows that did land.
//
// Returns one { success, status, message, data } per payload, in order. A network
// failure on one tyre is reported for that tyre (status 0) rather than rejecting
// the whole batch.
export async function saveTyres(payloads = [], { reason = 'import', concurrency = 4 } = {}) {
  const results = new Array(payloads.length);
  let next = 0;

  const lane = async () => {
    while (next < payloads.length) {
      const i = next++;
      try {
        results[i] = await request('/tyre', {
          method: 'POST',
          body: JSON.stringify(payloads[i])
        });
      } catch (error) {
        results[i] = { success: false, status: 0, message: error?.message || 'Network error.', data: null };
      }
    }
  };

  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, payloads.length)) }, lane));

  const saved = results.filter((r) => r && r.success).length;
  if (saved) emitTyreEvent(TYRE_EVENTS.CHANGED, { reason, count: saved });

  return results;
}

export async function associateTyreWithSensor(externalTyreId, payload, { reason = 'association' } = {}) {
  return mutate(`/tyre/${encodeURIComponent(externalTyreId)}/sensor-association`, {
    method: 'PUT',
    body: JSON.stringify({
      sensorId: payload.sensorId,
      ifExistingTyre: payload.ifExistingTyre || 'error'
    })
  }, { externalTyreId, reason });
}

export async function clearTyreSensorAssociation(externalTyreId, { reason = 'unassociation' } = {}) {
  return mutate(`/tyre/${encodeURIComponent(externalTyreId)}/sensor-association`, {
    method: 'PUT',
    body: JSON.stringify({
      sensorId: null,
      ifExistingTyre: 'error'
    })
  }, { externalTyreId, reason });
}

export async function updateTyreRecordInfo(externalTyreId, payload, { reason = 'info' } = {}) {
  return mutate(`/tyre/${encodeURIComponent(externalTyreId)}/info`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  }, { externalTyreId, reason });
}

export async function createTyreAction(externalTyreId, payload, { reason } = {}) {
  return mutate(`/tyre/${encodeURIComponent(externalTyreId)}/action`, {
    method: 'POST',
    body: JSON.stringify(payload)
  }, { externalTyreId, reason: reason || payload?.actionType || 'action' });
}

export async function fetchTyreActions(externalTyreId, params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });

  const qs = query.toString();

  return request(`/tyre/${encodeURIComponent(externalTyreId)}/action${qs ? `?${qs}` : ''}`, {
    method: 'GET'
  });
}
